import { useEffect, useMemo, useState } from "react";

import { Button, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ControlOutlined, UpOutlined } from "@ant-design/icons";

import ConfigurableDataTable from "../../shared/dashboards/ConfigurableDataTable/ConfigurableDataTable";
import AsyncExportButton from "../../shared/AsyncExport/AsyncExportButton";
import { AppStrings } from "../../../utils/constants/AppStrings";
import type { AsyncExportState } from "../../../queries/useAsyncExport.types";
import type {
  ProfitabilityMetricScope,
} from "../../../queries/sportsbook/profitabilityDetails";
import type { ReportingSummaryFilterGroup } from "../../../queries/sportsbook/reportingSummaryFilterOptions";
import type {
  ProfitabilityEventMetricsRow,
  ProfitabilitySportMetricsRow,
  ProfitabilitySummaryMetricsRow,
} from "../reportingSummaryTypes";
import { normalizeReportingSummaryMultiSelectValue } from "../reportingSummaryFiltersUtils";

const PAGE_SIZE_OPTIONS = [5, 10, 20] as const;
const DEFAULT_PAGE_SIZE = 10;

type ProfitabilityTableRow =
  | ProfitabilitySummaryMetricsRow
  | ProfitabilitySportMetricsRow
  | ProfitabilityEventMetricsRow;

function normalizeFilterSelection(
  current: string[],
  next: string | string[] | undefined,
  isMultiSelect: boolean,
): string[] {
  const values = Array.isArray(next) ? next : next ? [next] : [];
  return isMultiSelect
    ? normalizeReportingSummaryMultiSelectValue(current, values)
    : values;
}

export interface ProfitabilityTableSectionProps<
  Row extends ProfitabilityTableRow,
> {
  id: string;
  title: string;
  rows: Row[];
  columns: ColumnsType<Row>;
  paginated?: boolean;
  showRankingFilter?: boolean;
  loading?: boolean;
  metricScope?: ProfitabilityMetricScope;
  onMetricScopeChange?: (scope: ProfitabilityMetricScope) => void;
  betType?: string[];
  moneyType?: string[];
  betTypeOptions?: ReportingSummaryFilterGroup;
  moneyTypeOptions?: ReportingSummaryFilterGroup;
  onBetTypeChange?: (values: string[]) => void;
  onMoneyTypeChange?: (values: string[]) => void;
  exportState?: Pick<
    AsyncExportState<Record<string, unknown>>,
    "isBusy" | "start"
  >;
  buildExportPayload?: () => Record<string, unknown>;
}

function ProfitabilityTableSection<Row extends ProfitabilityTableRow>({
  id,
  title,
  rows,
  columns,
  paginated = false,
  showRankingFilter = false,
  loading = false,
  metricScope = "total",
  onMetricScopeChange,
  betType = [],
  moneyType = [],
  betTypeOptions,
  moneyTypeOptions,
  onBetTypeChange,
  onMoneyTypeChange,
  exportState,
  buildExportPayload,
}: ProfitabilityTableSectionProps<Row>) {
  const [expanded, setExpanded] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!paginated) return rows;
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [currentPage, pageSize, paginated, rows]);

  const exportColumns = useMemo(
    () =>
      columns.flatMap((column) => {
        if (
          !("dataIndex" in column) ||
          typeof column.dataIndex !== "string"
        ) {
          return [];
        }
        return [
          {
            key: column.dataIndex,
            label:
              typeof column.title === "string"
                ? column.title
                : column.dataIndex,
          },
        ];
      }),
    [columns],
  );

  return (
    <section
      className="reporting-summary-profitability-section"
      data-testid={`${id}-section`}
    >
      <button
        type="button"
        className="reporting-summary-profitability-section-toggle"
        aria-label={title}
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <span>{title}</span>
        <UpOutlined
          className={
            expanded
              ? "reporting-summary-profitability-chevron"
              : "reporting-summary-profitability-chevron reporting-summary-profitability-chevron--collapsed"
          }
        />
      </button>

      {expanded ? (
        <div className="reporting-summary-profitability-section-content">
          <div className="reporting-summary-profitability-toolbar">
            <div
              className={
                showRankingFilter
                  ? "reporting-summary-profitability-filters reporting-summary-profitability-filters--ranking"
                  : "reporting-summary-profitability-filters"
              }
            >
              {showRankingFilter ? (
                <Select
                  value="turnover"
                  className="reporting-summary-profitability-ranking-select"
                  aria-label={AppStrings.SPORTSBOOK_PROFITABILITY_BY_TURNOVER}
                  disabled
                  options={[
                    {
                      value: "turnover",
                      label: AppStrings.SPORTSBOOK_PROFITABILITY_BY_TURNOVER,
                    },
                  ]}
                />
              ) : null}
              <Select
                value={metricScope}
                aria-label={AppStrings.SPORTSBOOK_PROFITABILITY_PREMATCH_LIVE}
                options={[
                  { value: "total", label: AppStrings.TOTAL },
                  { value: "live", label: AppStrings.LIVE },
                  { value: "prematch", label: AppStrings.PRE_MATCH },
                ]}
                onChange={onMetricScopeChange}
                disabled={!onMetricScopeChange}
              />
              <Select
                mode={betTypeOptions?.multiSelect ? "multiple" : undefined}
                maxTagCount={1}
                placeholder={AppStrings.SPORTSBOOK_PROFITABILITY_BET_TYPE}
                aria-label={AppStrings.SPORTSBOOK_PROFITABILITY_BET_TYPE}
                options={betTypeOptions?.options ?? []}
                value={
                  betTypeOptions?.multiSelect ? betType : betType[0]
                }
                loading={loading}
                disabled={loading || !betTypeOptions}
                onChange={(next) =>
                  onBetTypeChange?.(
                    normalizeFilterSelection(
                      betType,
                      next,
                      Boolean(betTypeOptions?.multiSelect),
                    ),
                  )
                }
              />
              <Select
                placeholder={AppStrings.SPORTSBOOK_PROFITABILITY_CURRENCY_TYPE}
                aria-label={AppStrings.SPORTSBOOK_PROFITABILITY_CURRENCY_TYPE}
                options={moneyTypeOptions?.options ?? []}
                value={moneyType[0]}
                loading={loading}
                disabled={loading || !moneyTypeOptions}
                onChange={(next) =>
                  onMoneyTypeChange?.(
                    normalizeFilterSelection(moneyType, next, false),
                  )
                }
              />
            </div>
            <div className="reporting-summary-profitability-toolbar-actions">
              <Button
                aria-label={AppStrings.SPORTSBOOK_PROFITABILITY_TABLE}
                onClick={() => setSettingsOpen(true)}
              >
                {AppStrings.SPORTSBOOK_PROFITABILITY_TABLE}
                <ControlOutlined aria-hidden />

              </Button>
              {exportState && buildExportPayload ? (
                <AsyncExportButton
                  columns={exportColumns}
                  buildPayload={buildExportPayload}
                  exportState={exportState}
                  labels={{ buttonAriaLabel: AppStrings.DOWNLOAD }}
                  directExport
                />
              ) : null}
            </div>
          </div>

          <ConfigurableDataTable<Row>
            id={id}
            rowKey="key"
            className="reporting-summary-table reporting-summary-profitability-table"
            columns={columns}
            dataSource={visibleRows}
            loading={loading}
            locale={{ emptyText: AppStrings.NO_DATA_AVAILABLE }}
            fillHeight={false}
            settingsOpen={settingsOpen}
            onSettingsClose={() => setSettingsOpen(false)}
            paginationConfig={
              paginated
                ? {
                    currentPage,
                    pageSize,
                    total: rows.length,
                    pageSizeOptions: [...PAGE_SIZE_OPTIONS],
                    summaryPrefix: AppStrings.PAM_PAGINATION_RETURNING,
                    summaryConnector: AppStrings.PAM_PAGINATION_OF,
                    onPageChange: setCurrentPage,
                    onPageSizeChange: (value) => {
                      setPageSize(value);
                      setCurrentPage(1);
                    },
                  }
                : undefined
            }
            rowClassName={(_record: Row, index: number) =>
              index % 2 === 1 ? "reporting-summary-table-row--alt" : ""
            }
          />
        </div>
      ) : null}
    </section>
  );
}

export default ProfitabilityTableSection;
